<template>
  <div v-if="d" class="mt-4">
    <div class="d-flex align-items-center justify-content-between mb-3">
      <span style="font-size:14px;font-weight:700;text-transform:uppercase;letter-spacing:0.4px;color:rgba(253,71,25,0.85);">
        <i class="bi bi-gear-wide-connected text-primary mr-1"></i> Manufacturing
      </span>
      <a href="/manufacturing" class="text-primary" style="font-size:12px;font-weight:600;">
        Open manufacturing <i class="bi bi-chevron-right" style="font-size:10px;"></i>
      </a>
    </div>

    <div class="row" style="row-gap:20px;">
      <div class="col-12 col-xl-5">
        <div class="bg-white border h-100" :style="card">
          <div class="row" style="row-gap:18px;">
            <div v-for="s in stats" :key="s.label" class="col-6">
              <div class="text-secondary mb-1" style="font-size:12px;">
                {{ s.label }}
                <i v-if="s.help" class="bi bi-question-circle" style="font-size:11px;" :title="s.help"></i>
              </div>
              <span :class="s.danger ? 'text-danger' : 'text-dark'" style="font-size:18px;font-weight:700;">{{ s.value }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12 col-md-6 col-xl-4">
        <div class="bg-white border h-100" :style="card">
          <div class="mb-3" style="font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:0.4px;color:rgba(253,71,25,0.8);">Recent batches</div>
          <div v-for="b in batches" :key="b.order_number" class="d-flex justify-content-between align-items-center py-2 border-bottom" style="font-size:13px;gap:10px;">
            <div style="min-width:0;">
              <div class="text-dark text-truncate">{{ b.product || b.order_number }}</div>
              <div class="text-secondary" style="font-size:12px;">{{ qty(b.actual_batch_size) }} made · {{ b.efficiency != null ? b.efficiency + '% efficiency' : '' }}</div>
            </div>
            <div class="text-right text-nowrap">
              <div class="text-dark font-weight-bold">{{ money(b.unit_cost) }}</div>
              <div class="text-secondary" style="font-size:11px;">per unit</div>
            </div>
          </div>
          <p v-if="!batches.length" class="text-secondary mb-0" style="font-size:12px;">No batches finished in this period.</p>
        </div>
      </div>

      <div class="col-12 col-md-6 col-xl-3">
        <div class="bg-white border h-100" :style="card">
          <div class="d-flex align-items-center justify-content-between mb-3">
            <span style="font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:0.4px;color:rgba(253,71,25,0.8);">In production</span>
            <span class="badge badge-dark">{{ d.active_orders.length }}</span>
          </div>
          <div v-for="o in d.active_orders.slice(0, 5)" :key="o.id" class="d-flex justify-content-between align-items-center py-2 border-bottom" style="font-size:13px;gap:10px;">
            <span class="text-dark text-truncate">{{ (o.output_product && o.output_product.product) || o.order_number }}</span>
            <span class="badge badge-secondary text-capitalize">{{ String(o.status || '').replace('_', ' ') }}</span>
          </div>
          <p v-if="!d.active_orders.length" class="text-secondary mb-0" style="font-size:12px;">Nothing in production.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Manufacturing block on /analytics/beta: reuses the manufacturing module's
 * own analytics (GET /api/manufacturing/analytics/dashboard). Hidden when
 * the user lacks view-manufacturing-analytics (403).
 */
export default {
  name: 'ManufacturingBlock',

  props: {
    startDate: { type: String, required: true },
    endDate:   { type: String, required: true },
  },

  data() {
    return { d: null, card: 'border-radius:14px;border-color:#e9ecef !important;padding:20px;' };
  },

  computed: {
    stats() {
      const k = this.d.kpis;
      const variance = Number(k.cost_variance || 0);
      return [
        { label: 'Batches finished', value: k.completed_orders },
        { label: 'In progress', value: k.in_progress_orders },
        { label: 'Avg efficiency', value: (k.avg_efficiency || 0) + '%', help: 'Actual output ÷ planned output, for finished batches.' },
        { label: 'Production cost', value: this.money(k.total_actual_cost) },
        { label: 'Over / under plan', value: (variance > 0 ? '+' : '') + this.money(variance), danger: variance > 0, help: 'Actual cost minus planned cost for finished batches. Positive = cost more than planned.' },
        { label: 'Wastage', value: this.money(k.total_wastage_value), danger: k.abnormal_wastage_count > 0, help: `${k.abnormal_wastage_count} abnormal wastage record(s).` },
        { label: 'Materials running low', value: k.low_stock_count, danger: k.low_stock_count > 0 },
      ];
    },
    batches() {
      return (this.d.cost_trend || []).slice(0, 5);
    },
  },

  watch: {
    startDate() { this.load(); },
    endDate() { this.load(); },
  },

  mounted() { this.load(); },

  methods: {
    async load() {
      try {
        const { data } = await axios.get('/api/manufacturing/analytics/dashboard', {
          params: { from: this.startDate, to: this.endDate },
        });
        this.d = data;
      } catch (e) {
        this.d = null; // no permission or module off: block stays hidden
      }
    },
    qty(v) { return Number(v || 0).toLocaleString('en-US', { maximumFractionDigits: 2 }); },
    money(v) { return 'KES ' + Number(v || 0).toLocaleString('en-US', { maximumFractionDigits: 0 }); },
  },
};
</script>
